import { useState, useEffect, useCallback, useRef } from 'react';

type ConflictInfo = {
  path: string;
  localContent: string;
  externalContent: string;
  localTimestamp: number;
  externalTimestamp: number;
};

type ExternalChangeDetectionConfig = {
  storageKey: string;
  files: Record<string, string>;
  onConflict?: (conflicts: ConflictInfo[]) => void;
  onExternalChange?: (path: string, content: string) => void;
  autoReload?: boolean;
};

type ExternalChangeDetectionResult = {
  conflicts: ConflictInfo[];
  hasConflicts: boolean;
  resolveConflict: (path: string, keepLocal: boolean) => void;
  resolveAllConflicts: (keepLocal: boolean) => void;
  dismissConflicts: () => void;
  checkForChanges: () => void;
  lastExternalChangeTime: Date | null;
};

export function useExternalChangeDetection({
  storageKey,
  files,
  onConflict,
  onExternalChange,
  autoReload = false,
}: ExternalChangeDetectionConfig): ExternalChangeDetectionResult {
  const [conflicts, setConflicts] = useState<ConflictInfo[]>([]);
  const [lastExternalChangeTime, setLastExternalChangeTime] = useState<Date | null>(null);
  const localTimestamps = useRef<Record<string, number>>({});
  const filesRef = useRef(files);

  // Keep files ref updated
  useEffect(() => {
    filesRef.current = files;
    // Update local timestamps when files change
    const now = Date.now();
    Object.keys(files).forEach((path) => {
      if (!localTimestamps.current[path]) {
        localTimestamps.current[path] = now;
      }
    });
  }, [files]);

  // Check for external changes
  const checkForChanges = useCallback(() => {
    try {
      const storedData = localStorage.getItem(storageKey);
      if (!storedData) return;

      const parsed = JSON.parse(storedData);
      const externalFiles = parsed.files || parsed;
      const externalTimestamp = parsed.timestamp || Date.now();

      const newConflicts: ConflictInfo[] = [];
      const currentFiles = filesRef.current;

      Object.entries(externalFiles).forEach(([path, content]) => {
        if (typeof content !== 'string') return;

        const localContent = currentFiles[path];
        const localTimestamp = localTimestamps.current[path] || 0;

        // File exists both locally and externally
        if (localContent !== undefined && localContent !== content) {
          // External version is newer
          if (externalTimestamp > localTimestamp) {
            if (autoReload) {
              // Auto-reload: just apply external changes
              onExternalChange?.(path, content);
            } else {
              // Manual: add to conflicts
              newConflicts.push({
                path,
                localContent,
                externalContent: content,
                localTimestamp,
                externalTimestamp,
              });
            }
          }
        } else if (localContent === undefined && content) {
          // New file from external source
          onExternalChange?.(path, content);
        }
      });

      // Check for deleted files
      Object.keys(currentFiles).forEach((path) => {
        if (externalFiles[path] === undefined && currentFiles[path]) {
          // File was deleted externally
          newConflicts.push({
            path,
            localContent: currentFiles[path],
            externalContent: '',
            localTimestamp: localTimestamps.current[path] || 0,
            externalTimestamp,
          });
        }
      });

      if (newConflicts.length > 0) {
        setConflicts(newConflicts);
        setLastExternalChangeTime(new Date(externalTimestamp));
        onConflict?.(newConflicts);
      }
    } catch (error) {
      console.error('[ExternalChangeDetection] Error checking for changes:', error);
    }
  }, [storageKey, autoReload, onConflict, onExternalChange]);

  // Listen for storage events (changes from other tabs)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === storageKey && event.newValue) {
        console.log('[ExternalChangeDetection] Storage change detected');
        checkForChanges();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [storageKey, checkForChanges]);

  // Check for changes when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[ExternalChangeDetection] Tab visible, checking for changes');
        checkForChanges();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkForChanges]);

  // Check for changes on focus
  useEffect(() => {
    const handleFocus = () => {
      console.log('[ExternalChangeDetection] Window focused, checking for changes');
      checkForChanges();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [checkForChanges]);

  // Resolve a single conflict
  const resolveConflict = useCallback((path: string, keepLocal: boolean) => {
    setConflicts((prev) => {
      const conflict = prev.find((c) => c.path === path);
      if (!conflict) return prev;

      if (!keepLocal) {
        // Apply external change
        onExternalChange?.(path, conflict.externalContent);
      }

      // Update timestamp
      localTimestamps.current[path] = Date.now();

      return prev.filter((c) => c.path !== path);
    });
  }, [onExternalChange]);

  // Resolve all conflicts
  const resolveAllConflicts = useCallback((keepLocal: boolean) => {
    if (!keepLocal) {
      conflicts.forEach((conflict) => {
        onExternalChange?.(conflict.path, conflict.externalContent);
      });
    }

    // Update all timestamps
    const now = Date.now();
    conflicts.forEach((conflict) => {
      localTimestamps.current[conflict.path] = now;
    });

    setConflicts([]);
  }, [conflicts, onExternalChange]);

  // Dismiss all conflicts without resolving
  const dismissConflicts = useCallback(() => {
    setConflicts([]);
  }, []);

  // Mark local change timestamp
  const markLocalChange = useCallback((path: string) => {
    localTimestamps.current[path] = Date.now();
  }, []);

  // Update timestamp when files change
  useEffect(() => {
    Object.keys(files).forEach((path) => {
      markLocalChange(path);
    });
  }, [files, markLocalChange]);

  return {
    conflicts,
    hasConflicts: conflicts.length > 0,
    resolveConflict,
    resolveAllConflicts,
    dismissConflicts,
    checkForChanges,
    lastExternalChangeTime,
  };
}

export default useExternalChangeDetection;
